import * as Sentry from '@sentry/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCurrentUserFromLocalStorageContext,
  useEnvironmentServiceContext,
  useExecutorRoleFromLocalStorageContext,
  useLocalStorageDomainServiceContext,
} from '../../../../context/contexts';
import { GraphQLError } from '../../../errors';
import FallbackComponent from '../pages/Fallback';
import { ErrorTypes } from '../types';
import { getSentryPanalytTags } from '../utils';
import { dialogCommonOptions } from './display-utils';

export const UnauthedErrorBoundary = (props: {
  children?: React.ReactNode;
  tags?: Record<string, string | null>;
}) => {
  const environmentService = useEnvironmentServiceContext();
  const { t } = useTranslation();

  const domainService = useLocalStorageDomainServiceContext();
  const domainFromLocalStorage = domainService.get();
  const currentUserFromLocalStorage = useCurrentUserFromLocalStorageContext();
  const subDomain = environmentService.getSubDomain();
  const executorRoleFromLocalStorage = useExecutorRoleFromLocalStorageContext();

  const user = currentUserFromLocalStorage;
  const userInfo = user
    ? {
        email: user.email ?? 'UNKNOWN',
        username: user.displayName ?? 'UNKNOWN',
        name: user.displayName ?? 'UNKNOWN',
      }
    : {};

  const dialogOptions = {
    ...dialogCommonOptions(t),
    user: {
      email: userInfo.email,
      name: userInfo.name,
    },
  };

  const panalytTags = getSentryPanalytTags({
    subDomain,
    domain: null,
    domainFromLocalStorage,
    executorRole: null,
    executorRoleFromLocalStorage,
    simulatedRole: null,
    currentUser: null,
    currentUserFromLocalStorage,
    errorType: ErrorTypes.ERROR_BOUNDARY,
  });

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope, error) => {
        scope.setUser({
          ...scope.getUser(),
          ...userInfo,
        });
        let errorTags: Record<string, string> = {};
        if (error instanceof GraphQLError) {
          errorTags = {
            ...(error.tags ?? {}),
            errorType: error.errorType ?? ErrorTypes.ERROR_BOUNDARY,
          };
        }
        scope.setTags({ ...(props.tags ?? {}), ...panalytTags, ...errorTags });
      }}
      fallback={(errorData: {
        error: unknown;
        // componentStack: string;
        // eventId: string;
        resetError(): void;
      }) => {
        console.debug('UnauthedErrorBoundary hit!', errorData);
        return (
          <FallbackComponent
            error={errorData.error as Error}
            resetError={() => errorData.resetError()}
          />
        );
      }}
      showDialog
      dialogOptions={dialogOptions}
    >
      {props.children}
    </Sentry.ErrorBoundary>
  );
};
